import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRemoveDeselectedItems } from "@shared/utils/hooks/useRemoveDeselectedItems";
import { useSelector } from "react-redux";
import {
  BaseGetTradeExtraInfoDataRequest,
  BotData,
  DisplayResultMessage,
  FiltersSlice,
  GetTradeExtraInfoOrdersGraphByTrade,
  GetTradeExtraInfoOrdersResponse,
  GetTradesRequest,
  GridCardData,
  LanguageNameSpaces,
  NotificationShareTradeData,
  OpenMessage,
  OrderCardData,
  OrderTypes,
  PrecisionType,
  TimeRange,
  TradeActionType,
  TradeData,
  TradeExtraInfoActivity,
  TradeGraphData,
  TradeSourceType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectFormattedTradeExtraInfoOrdersGraphData } from "@shared/slices/trades.slice";
import { SkipToken, skipToken } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import {
  getActivitiesAnnotations,
  getTradeAnnotations,
  getTradeFulfilledAmount,
  getTradeTotalAmount,
  isEntryOrderType,
  isTakeProfitOrderType,
} from "@shared/features/trades/common/utils";
import {
  formatTimeSeriesChartData,
  getChangeValues,
} from "@shared/utils/charts";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import {
  CONTRACTS_EXCHANGES,
  DISPLAY_CURRENCIES,
} from "@shared/common/constants";
import {
  BulkCloseTradesStep,
  HandleTradeActionClick,
  OpenShareResultsModalType,
  TradeSource,
} from "@shared/features/trades/common/types";
import { BULK_CLOSE_TRADES_MUTATION_KEY } from "@shared/features/trades/common/constants";
import { JobToastType } from "@shared/slices/jobs.slice";
import { useAddJobToast } from "@shared/utils/hooks/useAddJobToast";
import { serializeFilters } from "@shared/features/filters/utils";
import useSocketAggTradeData from "@shared/features/trades/common/useSocketAggTradeData";
import { toFixed } from "@shared/utils/numbers";
import { useTheme } from "styled-components";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import moment from "moment";
import pluralize from "pluralize";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { useClientsData, useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { difference } from "lodash";

interface useTradeChartDataProps {
  tradeId?: number;
  timeRange?: TimeRange;
  activitiesArgs?: BaseGetTradeExtraInfoDataRequest | SkipToken;
  skipGraphData?: boolean;
}
export const useTradeChartData = ({
  tradeId,
  timeRange,
  activitiesArgs,
  skipGraphData,
}: useTradeChartDataProps) => {
  const baseArgs: BaseGetTradeExtraInfoDataRequest | typeof skipToken = useMemo(
    () =>
      tradeId
        ? {
            ignore_status: true,
            trade_id: tradeId,
          }
        : skipToken,
    [tradeId]
  );

  const getTradeExtraInfoOrdersGraphArgs:
    | GetTradeExtraInfoOrdersGraphByTrade
    | SkipToken = useMemo(
    () =>
      timeRange && baseArgs !== skipToken && !skipGraphData
        ? {
            ...baseArgs,
            time_range: timeRange,
          }
        : skipToken,
    [baseArgs, timeRange]
  );

  const getTradeExtraInfoOrdersGraphStatus =
    sharedLib.sharedApi.trades.useGetTradeExtraInfoOrdersGraphQuery(
      getTradeExtraInfoOrdersGraphArgs
    );

  const precisionConfig = getTradeExtraInfoOrdersGraphStatus?.data?.precision;
  const graphDataSelector = useMemo(() => {
    return selectFormattedTradeExtraInfoOrdersGraphData(
      getTradeExtraInfoOrdersGraphArgs,
      activitiesArgs
    );
  }, [activitiesArgs, getTradeExtraInfoOrdersGraphArgs]);

  const graphData = useSelector(graphDataSelector);

  return {
    getTradeExtraInfoOrdersGraphStatus,
    graphData,
    precisionConfig,
    graphChangeValues: getChangeValues(graphData),
  };
};

export const useTradePriceChart = (
  timeRange: TimeRange,
  tradeData: TradeData
) => {
  const args = useMemo(
    () => ({
      trade_id: tradeData.id,
      time_range: timeRange,
      ignore_status: true,
    }),
    [tradeData, timeRange]
  );

  const getPriceGraphDataStatus =
    sharedLib.sharedApi.common.useGetPriceGraphDataQuery(args);

  const { precision } = usePrecisions({
    symbol: tradeData.symbol,
    exchange: tradeData.exchange,
    precisionType: PrecisionType.PRICE,
  });

  const graphData = getPriceGraphDataStatus.data?.data.graph_data[timeRange];
  const lastPoint = graphData?.[graphData.length - 1];
  const formattedGraphData = useMemo(() => {
    return formatTimeSeriesChartData(
      graphData as any,
      2,
      true,
      null,
      precision
    );
  }, [graphData?.length, lastPoint?.[1], lastPoint?.[2], precision]);

  return {
    args,
    getPriceGraphDataStatus,
    formattedGraphData,
    precision,
  };
};

export const useTradesBulkUpdate = (
  handleTradeActionClick: HandleTradeActionClick,
  openMessage?: OpenMessage
) => {
  const { waitingCancellationTrades, bulkActionTrades } =
    useContext(TradesPageContext)!;
  const [selectedTradesIDs, setSelectedTradesIDs] = useState<number[]>([]);
  const [bulkCloseTradesModalOpen, setBulkCloseTradesModalOpen] =
    useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const { isSymbolActiveInAccount } = useSymbolsMetadata();
  const { getAccountIsReadOnly } = useClientsData();
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);

  useEffect(() => {
    if (selectedTradesIDs.length && !isBulkMode) setIsBulkMode(true);
  }, [selectedTradesIDs]);

  const tradeIDToTradeData = useMemo(() => {
    return bulkActionTrades?.reduce((acc, trade) => {
      acc[trade.id] = trade;
      return acc;
    }, {} as Record<number, TradeData>);
  }, [bulkActionTrades]);
  const bulkCancelTradesStatus =
    sharedLib.sharedApi.trades.useBulkCancelTradesMutation({
      fixedCacheKey: BULK_CLOSE_TRADES_MUTATION_KEY,
    })[1];

  const tradeIDs = useMemo(
    () => bulkActionTrades?.map((trade) => trade.id),
    [bulkActionTrades]
  );

  const tradeIDsSet = useMemo(() => new Set(tradeIDs), [tradeIDs]);

  const getTradeIsInactiveSymbol = useCallback(
    (tradeData?: TradeData) => {
      return !isSymbolActiveInAccount(tradeData?.symbol, tradeData?.client_id);
    },
    [isSymbolActiveInAccount]
  );

  const getTradeIsReadOnlyAccount = useCallback(
    (tradeData?: TradeData) => {
      return (
        !!tradeData?.client_id && getAccountIsReadOnly(tradeData.client_id)
      );
    },
    [getAccountIsReadOnly]
  );

  const getInactiveSymbolWarning = (numInvalid: number) => {
    const prefix =
      numInvalid === 1
        ? "The selected trade's symbol is"
        : `${numInvalid} of the selected trade's symbols are`;
    return `${prefix} currently inactive in the exchange and therefore the ${pluralize(
      "trade",
      numInvalid
    )} cannot be closed using bulk actions.`;
  };

  const waitingCancellationTradeIDsSet = useMemo(
    () => new Set(waitingCancellationTrades?.map((trade) => trade.id)),
    [waitingCancellationTrades]
  );
  const getRowSelectable = useCallback(
    (tradeData: TradeData) => {
      if (waitingCancellationTradeIDsSet.has(tradeData.id)) {
        return "You can't select trades that are waiting for close confirmation from the exchange";
      } else if (getTradeIsReadOnlyAccount(tradeData)) {
        return t("warnings.read_only_account_trade_bulk");
      } else if (getTradeIsInactiveSymbol(tradeData)) {
        return getInactiveSymbolWarning(1);
      }
    },
    [
      waitingCancellationTradeIDsSet,
      getTradeIsInactiveSymbol,
      getTradeIsReadOnlyAccount,
    ]
  );

  const validTradeIDs = useMemo(() => {
    return tradeIDs?.filter((tradeId) => {
      const tradeData = tradeIDToTradeData?.[tradeId];
      return !!tradeData && !getRowSelectable(tradeData);
    });
  }, [tradeIDs, getRowSelectable]);

  const actionButtonsData = [
    {
      text: "Close Trades",
      onClick: () =>
        handleTradeActionClick(TradeActionType.CLOSE_TRADE, undefined, () =>
          setBulkCloseTradesModalOpen(true)
        ),
    },
  ];

  const exitBulkMode = () => {
    setSelectedTradesIDs([]);
    setIsBulkMode(false);
  };

  const handleSelectTrades = (tradesIds: number[], isSelectAll = false) => {
    const readOnlyAccountTradesIds = tradesIds.filter((tradeId) => {
      const tradeData = tradeIDToTradeData?.[tradeId];
      return getTradeIsReadOnlyAccount(tradeData);
    });
    if (readOnlyAccountTradesIds.length) {
      if (!isSelectAll) {
        openMessage?.(t("warnings.read_only_account_trade_bulk"), "warning");
      }
    }
    const tradesIdsWithoutReadOnly = difference(
      tradesIds,
      readOnlyAccountTradesIds
    );
    const inactiveSymbolsTradesIds = tradesIdsWithoutReadOnly.filter(
      (tradeId) => {
        return getTradeIsInactiveSymbol(tradeIDToTradeData?.[tradeId]);
      }
    );
    if (inactiveSymbolsTradesIds.length && !isSelectAll) {
      openMessage?.(
        getInactiveSymbolWarning(inactiveSymbolsTradesIds.length),
        "warning"
      );
    }
    const updatedSelectedIds = difference(
      tradesIdsWithoutReadOnly,
      inactiveSymbolsTradesIds
    );
    setSelectedTradesIDs(updatedSelectedIds);
  };

  const selectAll = () => handleSelectTrades([...tradeIDsSet], true);

  useRemoveDeselectedItems(
    selectedTradesIDs,
    tradeIDsSet,
    setSelectedTradesIDs,
    "trades",
    openMessage
  );

  const selectedTradeIdsSet = useMemo(
    () => new Set(selectedTradesIDs),
    [selectedTradesIDs]
  );

  const isAllRowsSelected = useMemo(() => {
    return (
      !!validTradeIDs?.length &&
      validTradeIDs.every((tradeId) => selectedTradeIdsSet.has(tradeId))
    );
  }, [selectedTradesIDs, validTradeIDs]);

  const onFinishBulkCloseTrades = () => {
    setSelectedTradesIDs([]);
  };

  const closeBulkCloseTradesModal = () => setBulkCloseTradesModalOpen(false);

  return useMemo(
    () => ({
      selectedTradesIDs,
      setSelectedTradesIDs: handleSelectTrades,
      setBulkCloseTradesModalOpen,
      bulkCloseTradesModalOpen,
      tradeIDToTradeData,
      isLoadingBulkUpdate: bulkCancelTradesStatus.isLoading,
      getRowSelectable,
      actionButtonsData,
      isBulkMode,
      setIsBulkMode,
      exitBulkMode,
      selectAll,
      closeBulkCloseTradesModal,
      onFinishBulkCloseTrades,
      isAllRowsSelected,
    }),
    [
      selectedTradesIDs,
      bulkCloseTradesModalOpen,
      tradeIDToTradeData,
      bulkCancelTradesStatus.isLoading,
      isBulkMode,
      getRowSelectable,
      isSymbolActiveInAccount,
      isAllRowsSelected,
    ]
  );
};

interface UseBulkUpdateTradesModalProps {
  tradeIDs?: number[];
  tradeIDToTradeData?: Record<number, TradeData>;
  open?: boolean;
  closeModal?: () => void;
  sellCoins?: boolean;
  setSellCoins: (sellCoins: boolean) => void;
  onFinish?: () => void;
}
export const useBulkUpdateTradesModal = ({
  tradeIDs,
  tradeIDToTradeData,
  closeModal,
  open,
  sellCoins,
  setSellCoins,
  onFinish,
}: UseBulkUpdateTradesModalProps) => {
  const { getInvalidAccountData } = useInvalidAccount();

  const hasFilledEntries = useMemo(() => {
    return tradeIDs?.some((tradeId) => {
      return (tradeIDToTradeData?.[tradeId]?.invested_amount || 0) > 0;
    });
  }, [tradeIDs]);

  const numInactiveAccounts = useMemo(() => {
    return tradeIDs?.filter((tradeId) => {
      const tradeData = tradeIDToTradeData?.[tradeId];
      return tradeData && !!getInvalidAccountData(tradeData.client_id).color;
    }).length;
  }, [tradeIDs, getInvalidAccountData]);

  const getDefaultStep = () => {
    if (numInactiveAccounts)
      return BulkCloseTradesStep.CONFIRM_INACTIVE_ACCOUNT;
    if (hasFilledEntries) return BulkCloseTradesStep.EXISTING_COINS;
    return BulkCloseTradesStep.CONFIRM;
  };

  const [step, setStep] = useState(getDefaultStep());

  useEffect(() => {
    if (open) {
      setStep(getDefaultStep());
    }
  }, [open]);

  const { addJobToast, removeJobToast } = useAddJobToast(JobToastType.Trade);
  const [bulkCancelTrades, bulkCancelTradesStatus] =
    sharedLib.sharedApi.trades.useBulkCancelTradesMutation({
      fixedCacheKey: BULK_CLOSE_TRADES_MUTATION_KEY,
    });
  const isLoading = bulkCancelTradesStatus.isLoading;

  const onCloseTrades = () => {
    if (tradeIDs) {
      const args = { trade_ids: tradeIDs, ratio: sellCoins ? 100 : 0 };
      const id = addJobToast(args);
      onCloseModal();
      bulkCancelTrades(args)
        .unwrap()
        .catch(() => {})
        .finally(() => {
          removeJobToast(id);
          onFinish?.();
        });
    }
  };

  const onCloseModal = () => {
    closeModal?.();
    setTimeout(() => {
      setStep(BulkCloseTradesStep.EXISTING_COINS);
      setSellCoins(true);
    }, 150);
  };

  const onOkPress = () => {
    if (step === BulkCloseTradesStep.CONFIRM_INACTIVE_ACCOUNT) {
      if (hasFilledEntries) setStep(BulkCloseTradesStep.EXISTING_COINS);
      else onCloseTrades();
    }
    if (step === BulkCloseTradesStep.EXISTING_COINS)
      setStep(BulkCloseTradesStep.CONFIRM);
    else if (step === BulkCloseTradesStep.CONFIRM) {
      onCloseTrades();
    }
  };

  return {
    onOkPress,
    step,
    isLoading,
    onCloseModal,
    modalTitle: `Close ${tradeIDs?.length || 0} Trades`,
    okText: `${
      step === BulkCloseTradesStep.CONFIRM ? "Yes, " : ""
    }Close Trades`,
    cancelText: "Cancel",
    numInactiveAccounts,
  };
};

export const useGetClosedTradesArgs = (
  args: GetTradesRequest | undefined | typeof skipToken,
  filtersSlice: FiltersSlice
) => {
  const filtersSliceState = useSelector(filtersSlice.selectors.selectFilters);
  if (args === skipToken) return skipToken;
  const serializedFilters = serializeFilters(filtersSliceState);
  return filtersSliceState.loadedFromQuery
    ? {
        ...args,
        filters: serializedFilters,
        should_limit: true,
      }
    : skipToken;
};

interface useGetTradesQueriesProps {
  openTradesArgs: GetTradesRequest | undefined | typeof skipToken;
  closedTradesArgs?: GetTradesRequest | undefined | typeof skipToken;
  getOpen: boolean;
  getClosed: boolean;
  delayUpdateOnClose?: number;
  useOnReconnect?: (refetch?: () => void) => void;
  inDrawer?: boolean;
}
export const useGetTradesQueries = ({
  openTradesArgs,
  closedTradesArgs,
  getOpen,
  getClosed,
  delayUpdateOnClose,
  useOnReconnect,
  inDrawer,
}: useGetTradesQueriesProps) => {
  const options = inDrawer ? { refetchOnMountOrArgChange: true } : undefined;
  const getClosedTradesFilterStatus =
    sharedLib.sharedApi.trades.useGetClosedTradesFiltersQuery(
      getClosed ? { inDrawer } : skipToken,
      options
    );
  const getOpenTradesStatus =
    sharedLib.sharedApi.trades.useGetOpenTradesDataQuery(
      getOpen ? openTradesArgs : skipToken,
      options
    );
  const getClosedTradesStatus =
    sharedLib.sharedApi.trades.useGetClosedTradesDataQuery(
      getClosed ? closedTradesArgs : skipToken,
      options
    );

  useOnReconnect?.(getOpen ? getOpenTradesStatus.refetch : undefined);

  const socketArgs = useMemo(
    () =>
      getOpen
        ? {
            fetch_initial_data: !!getOpenTradesStatus.data,
            as_dict: true,
          }
        : skipToken,
    [getOpen]
  );

  const socketTradesData = useSocketAggTradeData(
    socketArgs,
    openTradesArgs,
    closedTradesArgs,
    delayUpdateOnClose,
    getOpenTradesStatus.refetch
  );

  return {
    getOpenTradesStatus,
    getClosedTradesStatus,
    getClosedTradesFilterStatus,
    socketTradesData,
  };
};

interface useMoveToCurrentPriceModalProps {
  tradeData?: TradeData;
  displayResultMessage: DisplayResultMessage;
  closeModal?: () => void;
}
export const useMoveToCurrentPriceModal = ({
  tradeData,
  displayResultMessage,
  closeModal,
}: useMoveToCurrentPriceModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const tradeId = tradeData?.id;
  const { data: firstEntry, ...getFirstEntryStatus } =
    sharedLib.sharedApi.trades.useGetFirstEntryQuery(
      tradeId ? { trade_id: tradeId, ignore_status: true } : skipToken,
      { refetchOnMountOrArgChange: true }
    );
  const [moveEntryToCurrentPrice, moveEntryToCurrentPriceStatus] =
    sharedLib.sharedApi.trades.useMoveEntryToCurrentPriceMutation();
  const { precision: pricePrecision } = usePrecisions(
    tradeData
      ? {
          symbol: tradeData.symbol,
          exchange: tradeData.exchange,
          precisionType: PrecisionType.PRICE,
        }
      : undefined
  );

  const onSubmit = () => {
    if (firstEntry && tradeId) {
      displayResultMessage(
        moveEntryToCurrentPrice({
          trade_id: tradeId,
          order_id: firstEntry.id,
          ignore_status: true,
        }).unwrap(),
        t("success.nearest_entry_moved")
      )
        .then(closeModal)
        .catch(() => {});
    }
  };

  const firstEntryPrice = firstEntry?.price;
  const description = firstEntry
    ? `This action will move the trade's nearest entry to the current price. Are you sure you wish to continue?`
    : undefined;

  return {
    onSubmit,
    title: "Move Nearest Entry",
    contentIsLoading: getFirstEntryStatus.isLoading,
    actionIsLoading: moveEntryToCurrentPriceStatus.isLoading,
    description,
    firstEntryPrice,
    pricePrecision,
  };
};

interface useSellPartiallyModalProps {
  tradeData?: TradeData;
  displayResultMessage: DisplayResultMessage;
  openMessage: OpenMessage;
  closeModal?: () => void;
}
export const useSellPartiallyModal = ({
  tradeData,
  displayResultMessage,
  openMessage,
  closeModal,
}: useSellPartiallyModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const tradeId = tradeData?.id;
  const [quantity, setQuantity] = useState<string | undefined>("0");
  const [sellPartialTrade, sellPartialTradeStatus] =
    sharedLib.sharedApi.trades.useSellPartialTradeMutation();
  const { data: tradeQuantityLimits, ...getTradeQuantityLimitsStatus } =
    sharedLib.sharedApi.trades.useGetTradeQuantityLimitsQuery(
      tradeId ? { trade_id: tradeId, ignore_status: true } : skipToken,
      { refetchOnMountOrArgChange: true }
    );
  const { precision } = usePrecisions(
    tradeData
      ? {
          symbol: tradeData.symbol,
          exchange: tradeData.exchange,
          precisionType: PrecisionType.QUANTITY,
        }
      : undefined
  );
  const { getQuantityCoin } = useSymbolsMetadata();

  const defaultPercent = 50;
  const maxPercent = 75;
  const minQuantityData = tradeQuantityLimits?.amount;
  const minQuantity =
    minQuantityData?.min !== undefined
      ? toFixed(minQuantityData?.min, precision, true)
      : undefined;
  const availableQuantity =
    minQuantityData?.max !== undefined
      ? toFixed(minQuantityData?.max, precision, true)
      : undefined;
  const maxQuantity =
    minQuantityData?.max !== undefined
      ? toFixed((minQuantityData?.max * maxPercent) / 100, precision)
      : undefined;

  useEffect(() => {
    if (tradeData && minQuantity !== undefined && maxQuantity !== undefined) {
      if (minQuantity >= maxQuantity) {
        openMessage(t("warnings.amount_below_minimum"), "error");
        closeModal?.();
      } else if (availableQuantity !== undefined) {
        setQuantity(
          toFixed(
            (availableQuantity * defaultPercent) / 100,
            precision
          ).toString()
        );
      }
    }
  }, [minQuantity, maxQuantity]);

  const quantityNumber = parseFloat(quantity || "0");
  const isValid = minQuantity !== undefined && quantityNumber >= minQuantity;
  const quantityCoin = getQuantityCoin(tradeData?.symbol, tradeData?.exchange);
  const errorMessage = isValid
    ? undefined
    : `Quantity should be at least ${minQuantity}`;
  const onSubmit = () => {
    if (tradeId && isValid) {
      displayResultMessage(
        sellPartialTrade({
          trade_id: tradeId,
          quantity: quantityNumber,
          ignore_status: true,
        }).unwrap(),
        t("success.partial_sell_completed")
      )
        .then(closeModal)
        .catch(() => {});
    }
  };

  const isContractsExchange =
    tradeData?.exchange && CONTRACTS_EXCHANGES.has(tradeData.exchange);
  const description = `How many of the trade ${
    isContractsExchange ? "contracts" : "coins"
  } do you wish to sell?`;

  return {
    onSubmit,
    title: "Sell Partially",
    actionIsLoading: sellPartialTradeStatus.isLoading,
    description,
    contentIsLoading: getTradeQuantityLimitsStatus.isLoading,
    quantityPrecision: precision,
    maxPercent,
    quantity,
    setQuantity,
    quantityCoin,
    isValid,
    okText: `Sell ${isContractsExchange ? "Contracts" : "Coins"}`,
    errorMessage,
    availableQuantity,
    defaultPercent,
  };
};

interface useTradeChartAnnotationsProps {
  ordersData: GetTradeExtraInfoOrdersResponse | undefined;
  activities: TradeExtraInfoActivity[] | undefined;
  botData?: BotData;
  pricePrecision: number;
  gridCardsData: GridCardData[] | undefined;
  isOpenTrade?: boolean;
  isGridBotTrade?: boolean;
}
export const useTradeChartAnnotations = ({
  ordersData,
  activities,
  pricePrecision,
  gridCardsData,
  isOpenTrade,
  isGridBotTrade,
}: useTradeChartAnnotationsProps) => {
  const theme = useTheme();

  const activitiesAnnotations = useMemo(() => {
    return getActivitiesAnnotations(activities || [], theme);
  }, [activities, theme]);

  const ordersAnnotations = useMemo(() => {
    if (!isOpenTrade) return [];
    let entries: OrderCardData[] | GridCardData[] | undefined;
    let takeProfits: OrderCardData[] | GridCardData[] | undefined;
    let { stop_order_cards: stop } = ordersData?.data || {};
    if (isGridBotTrade) {
      entries = gridCardsData?.filter((gridData) =>
        isEntryOrderType(gridData.pending_order_dict?.type)
      );
      takeProfits = gridCardsData?.filter((gridData) =>
        isTakeProfitOrderType(gridData.pending_order_dict?.type)
      );
    } else {
      entries = ordersData?.data.entry_order_cards;
      takeProfits = ordersData?.data.take_profit_order_cards;
    }
    return getTradeAnnotations(
      theme,
      entries,
      takeProfits,
      stop,
      pricePrecision,
      !isGridBotTrade,
      ordersData?.data.current_price
    );
  }, [theme, ordersData, gridCardsData, pricePrecision, isOpenTrade]);

  const tradeAnnotations = useMemo(() => {
    return [...activitiesAnnotations, ...ordersAnnotations];
  }, [activitiesAnnotations, ordersAnnotations]);

  return {
    activitiesAnnotations,
    tradeAnnotations,
  };
};

export const useTradeStatistics = (selectedAmount?: number) => {
  const { ordersData, symbol, exchange, statsData, isGridBotTrade } =
    useContext(TradeDetailsContext)!;
  const displayCurrency = useSelector(selectDisplayCurrency);
  const currentPrice = ordersData?.data.current_price;
  const entries = ordersData?.data.entry_order_cards;
  const { getPrecision } = usePrecisions();

  const amountPrecision = getPrecision(PrecisionType.AMOUNT, symbol, exchange);
  const amountPrecisionKey =
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? PrecisionType.USD_AMOUNT
      : PrecisionType.BTC_AMOUNT;
  const displayCurrencyAmountPrecision = getPrecision(
    amountPrecisionKey,
    symbol,
    exchange
  );

  const totalAmount = useMemo(() => {
    if (selectedAmount !== undefined) return selectedAmount;
    return getTradeTotalAmount(entries || [], amountPrecision || 0);
  }, [selectedAmount, entries, amountPrecision]);

  const fulfilledAmount = useMemo(() => {
    return getTradeFulfilledAmount(entries || [], amountPrecision || 0);
  }, [entries, amountPrecision]);

  return {
    currentPrice,
    isGridBotTrade,
    displayCurrencyAmountPrecision,
    totalAmount,
    fulfilledAmount,
    amountPrecision,
    pnlText: statsData?.change[displayCurrency],
    symbol,
    exchange,
  };
};

interface useCancelTradeModalProps {
  tradeId?: number;
  tradeData?: TradeData;
  closeModal: () => void;
  showCloseOptions?: boolean;
  onSuccess?: () => void;
  displayResultMessage: DisplayResultMessage;
  isVisible?: boolean;
  accountIsValid?: boolean;
  symbol?: string;
  accountId?: number;
}
export const useCancelTradeModal = ({
  tradeData,
  tradeId: propsTradeId,
  closeModal,
  showCloseOptions: propsShowCloseOptions,
  onSuccess,
  displayResultMessage,
  isVisible,
  accountIsValid: propsAccountIsValid = true,
  symbol,
  accountId,
}: useCancelTradeModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [cancelTrade, { isLoading, isSuccess }] =
    sharedLib.sharedApi.trades.useCancelTradeMutation();
  const { getInvalidAccountData } = useInvalidAccount();
  const { isSymbolActiveInAccount } = useSymbolsMetadata();
  const isSymbolInactive = !isSymbolActiveInAccount(symbol, accountId);
  const showCloseOptions =
    isSymbolInactive ||
    (tradeData ? tradeData?.invested_quantity > 0 : propsShowCloseOptions);

  const getDefaultStep = () => {
    let accountIsInvalid: boolean | undefined = !propsAccountIsValid;
    if (tradeData?.client_id) {
      accountIsInvalid = getInvalidAccountData(
        tradeData?.client_id
      ).isInvalidOrInactive;
    }
    if (accountIsInvalid) {
      return BulkCloseTradesStep.CONFIRM_INACTIVE_ACCOUNT;
    } else if (showCloseOptions) return BulkCloseTradesStep.CONFIRM;
  };

  const [step, setStep] = useState(getDefaultStep());
  const tradeId = tradeData?.id ?? propsTradeId;

  useEffect(() => {
    if (isVisible) {
      setStep(getDefaultStep());
    }
  }, [isVisible]);

  const onCancel = () => {
    closeModal();
  };

  const onCancelTrade = (sellCoins: boolean) => {
    if (tradeId) {
      displayResultMessage(
        cancelTrade({ trade_id: tradeId, ratio: sellCoins ? 100 : 0 }).unwrap(),
        t("success.trade_closed"),
        t("errors.close_failed")
      ).catch(() => {});
    }
  };

  useEffect(() => {
    if (isSuccess) {
      onCancel();
      onSuccess?.();
    }
  }, [isSuccess]);

  return {
    showCloseOptions,
    isLoading,
    onCancel,
    onCancelTrade,
    step,
  };
};

export const useShareTradeResults = (
  displayResultMessage: DisplayResultMessage,
  openMessage: OpenMessage,
  setShareTradeData?: (shareTradeData: NotificationShareTradeData) => void
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const [isShareResultsModalVisible, setIsShareResultsModalVisible] =
    useState(false);
  const [symbol, setSymbol] = useState<string>();

  const [
    getShareTradeImage,
    {
      data: shareTradeImageUrl,
      isLoading: shareTradeImageLoading,
      originalArgs: shareTradeImageArgs,
    },
  ] = sharedLib.sharedApi.trades.useGetShareTradeImageMutation();

  const getShareResultsErrorMessage = (
    tradeSource?: TradeSourceType | TradeSource,
    clientIsInvalid?: boolean
  ) => {
    if (
      tradeSource === TradeSourceType.GRID_BOT ||
      tradeSource === TradeSource.GRID_BOT
    ) {
      return t("share_trade_results.warnings.grid_bot_share");
    } else if (clientIsInvalid) {
      return t("share_trade_results.warnings.inactive_account_share");
    }
  };

  const openShareResultsModal: OpenShareResultsModalType = (
    tradeId,
    symbol,
    tradeSource,
    clientIsInvalid,
    onSuccess,
    openModalDelay
  ) => {
    const error = getShareResultsErrorMessage(tradeSource, clientIsInvalid);
    if (error) {
      openMessage(error, "warning");
    } else {
      setSymbol(symbol);
      displayResultMessage(
        getShareTradeImage({
          trade_id: tradeId,
          ignore_status: true,
        }).unwrap()
      )
        .then((response) => {
          if (setShareTradeData) {
            const imageTitle = `${symbol?.replace("/", "")}-${formattedDate}`;
            if (response) {
              setShareTradeData({ imageTitle, imageUrl: response });
            }
          } else {
            onSuccess?.();
            setTimeout(
              () => setIsShareResultsModalVisible(true),
              openModalDelay ?? 0
            );
          }
        })
        .catch(() => {});
    }
  };

  const closeShareResultsModal = useCallback(() => {
    setIsShareResultsModalVisible(false);
  }, []);

  const formattedDate = moment().format("YYYY_MM_DD-HH_mm");
  const imageTitle = `${symbol?.replace("/", "")}-${formattedDate}`;

  return {
    isShareResultsModalVisible,
    shareTradeImageUrl,
    shareTradeImageLoading,
    openShareResultsModal,
    closeShareResultsModal,
    imageTitle,
    getShareResultsErrorMessage,
    shareTradeImageArgs,
  };
};

export const useShareResultData = (tradeData: TradeData) => {
  const {
    shareTradeImageLoading,
    shareTradeImageArgs,
    getShareResultsErrorMessage,
    handleTradeActionClick,
  } = useContext(TradeActionsContext)!;

  const { getInvalidAccountData } = useInvalidAccount();
  const invalidAccountData = getInvalidAccountData(tradeData?.client_id);
  const isInvalid = !!getShareResultsErrorMessage(
    tradeData?.source_type,
    invalidAccountData?.isInvalidOrInactive
  );
  const showSpin =
    shareTradeImageLoading && shareTradeImageArgs?.trade_id === tradeData.id;
  const showAnimation =
    !isInvalid &&
    (tradeData.graph_data as TradeGraphData).profit >=
      (tradeData.margin_type ? 10 : 5);

  const onShareClick = (openShareModalDelay?: number) => {
    handleTradeActionClick(
      TradeActionType.SHARE_RESULTS,
      tradeData,
      undefined,
      openShareModalDelay
    );
  };

  return {
    isInvalid,
    showSpin,
    showAnimation,
    onShareClick,
    shareTradeImageLoading,
  };
};

interface useInactiveAccountWarningProps {
  isBots?: boolean;
  numItems: number;
  numInvalidItems: number;
}
export const useInactiveAccountWarning = ({
  isBots,
  numItems,
  numInvalidItems,
}: useInactiveAccountWarningProps) => {
  const isPlural = numInvalidItems > 1;
  const isAllInvalid = numItems === numInvalidItems;
  const objectName = isBots ? "bot" : "trade";
  const pluralizedTradesText = pluralize("trade", numItems);
  const pluralizedIsText = pluralize("is", numInvalidItems);
  const pluralizedTheirText = isPlural ? "their" : "it's";

  const getTheseTradesText = (isTitle?: boolean) => {
    const numItemsToUse = isTitle ? numItems : numInvalidItems;
    const isPlural = numItemsToUse > 1;
    const pluralizedThisText = pluralize("this", numItemsToUse);
    const pluralizedObjectName = pluralize(objectName, numItemsToUse);
    const pluralizedTradesText = pluralize("trade", numItemsToUse);
    return isBots
      ? `${pluralizedThisText} ${pluralizedObjectName}${
          isPlural ? "'" : "'s"
        } ${isTitle ? "open " : ""}${pluralizedTradesText}`
      : `${pluralizedThisText} ${pluralizedObjectName}`;
  };

  let prefix: string;
  if (isBots) {
    if (isAllInvalid) {
      prefix = `Please note that ${getTheseTradesText()} ${pluralizedIsText} no longer managed and ${pluralizedTheirText} orders`;
    } else {
      prefix = `Please note that ${numInvalidItems} of the selected bots' trades ${pluralizedIsText} no longer managed and ${pluralizedTheirText} orders`;
    }
  } else {
    if (isAllInvalid) {
      prefix = `Please note that the orders related to ${getTheseTradesText()}`;
    } else {
      prefix = `Please note that ${numInvalidItems} of the selected ${pluralizedTradesText} ${
        isPlural ? "are" : "is"
      } no longer managed and ${isPlural ? "their" : "it's"} related orders`;
    }
  }

  return {
    prefix,
    title: `Do you wish to close ${getTheseTradesText(true)} on Cornix?`,
  };
};

interface useTradeCardExtraInfoProps {
  data: TradeData;
  isOpenTrades?: boolean;
}

export const useTradeCardExtraInfo = ({
  data,
  isOpenTrades,
}: useTradeCardExtraInfoProps) => {
  const theme = useTheme();
  const { precision: pricePrecision } = usePrecisions({
    precisionType: PrecisionType.PRICE,
    symbol: data.symbol,
    exchange: data.exchange,
  });
  const { getInvalidAccountData } = useInvalidAccount();
  const invalidAccountData = getInvalidAccountData(data.client_id);

  const getTradeType = () => {
    const tradeType: string = data["Long/Short"];
    const marginType = data.margin_type;

    if (marginType && data.leverage) {
      if (isOpenTrades) return `${tradeType} - ${marginType} ${data.leverage}X`;
      else if (data.leverage > 1) return `${tradeType} - ${data.leverage}X`;
    }

    return tradeType;
  };

  const orders = {
    [OrderTypes.ENTRY]: data.next_entry,
    [OrderTypes.TAKE_PROFIT]: data.next_take_profit,
    [OrderTypes.STOP]: data.next_stop,
  };

  return { theme, pricePrecision, invalidAccountData, orders, getTradeType };
};
